import '.a{display:grid;}.ssr-plugin'
import '.aq{align-items:flex-start;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.ar{flex-wrap:wrap;}.ssr-plugin'
import '.f9{gap:0.75rem;}.ssr-plugin'
import '.dp{border-radius:2rem;}.ssr-plugin'
import '.h9{padding:0.5rem 1rem;}.ssr-plugin'
import '.a1{cursor:pointer;}.ssr-plugin'
import '.b8{font-size:0.875rem;}.ssr-plugin'
import '.i7{font-weight:600;}.ssr-plugin'
import '.ae{flex-shrink:0;}.ssr-plugin'
import '.a3{color:var(--brand-secondary);}.ssr-plugin'
import { useMemo } from 'react'
import { useQuery, useAuthState } from '@based/sdk/react'
import { styled } from 'inlines'
import Column from '@app/components/layout/Column'
import { Select } from '@app/components/core/input/select/Select'
import { Button } from '@app/components/core/button/Button'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import { PostCategory } from '@schema/postCategory'
import { ListRegionModel } from 'functions/app/geography/list/region'
import useMediaQuery from '@app/util/useMediaQuery'

export type DiscussionFiltersValue = {
  categoryFilter: 'all' | 'my-posts' | number
  regionFilter: string | null
}

export type DiscussionFiltersProps = {
  showCategories?: boolean
  showLocationFilters?: boolean
  value: DiscussionFiltersValue
  onFiltersChange: (filters: DiscussionFiltersValue) => void
}

type PostCategoryOption = Pick<
  PostCategory,
  'id' | 'name' | 'primaryColour'
>

export const DiscussionFilters = ({
  showCategories = true,
  showLocationFilters = true,
  value,
  onFiltersChange,
}: DiscussionFiltersProps) => {
  const t = useTranslations().app.discussions
  const authState = useAuthState()
  const isMobile = useMediaQuery()

  const { categoryFilter, regionFilter } = value

  const { data: categories = [] } = useQuery('list-post-categories')
  const { data: regions = [] } = useQuery(showLocationFilters ? 'list-region' : null)

  const categoryOptions = useMemo(
    () => (Array.isArray(categories) ? categories : []) as PostCategoryOption[],
    [categories],
  )

  const regionOptions = useMemo(
    () => (Array.isArray(regions) ? regions : []) as ListRegionModel[],
    [regions],
  )

  const hasActiveFilters =
    categoryFilter !== 'all' || regionFilter !== null

  const updateCategoryFilter = (next: 'all' | 'my-posts' | number) => {
    onFiltersChange({ categoryFilter: next, regionFilter })
  }

  const updateRegionFilter = (next: string | null) => {
    onFiltersChange({ categoryFilter, regionFilter: next })
  }

  const clearFilters = () => {
    onFiltersChange({ categoryFilter: 'all', regionFilter: null })
  }

  return (
    <Column gap="0.75rem" alignItems="stretch">
      <div className="a aq n"
        style={{
          gridTemplateColumns: isMobile
            ? '1fr'
            : showCategories && showLocationFilters
              ? 'minmax(0, 9fr) minmax(0, 3fr)'
              : '1fr',
          gap: isMobile ? '1.5rem' : '2rem',}}
      >
        {showCategories && (
          <Column gap="0.5rem" alignItems="flex-start" style={{ minWidth: 0 }}>
            <Text.Medium isBold={true} style={{ flexShrink: 0 }}>
              {t.filterByCategory}
            </Text.Medium>
            <div className="d ar f9 n"
            >
              {categoryOptions.map((category) => {
                const isSelected = categoryFilter === category.id
                return (
                  <button className="dp h9 a1 b8 i7 ae"
                    key={category.id}
                    type="button"
                    onClick={() =>
                      updateCategoryFilter(isSelected ? 'all' : category.id)
                    }
                    style={{
                      border: `1px solid ${isSelected ? category.primaryColour : 'var(--gray-200)'}`,
                      backgroundColor: isSelected
                        ? category.primaryColour
                        : 'var(--palette-white)',}}
                  >
                    {category.name}
                  </button>
                )
              })}
              {authState.userId && (
                <button className="dp a3 h9 a1 b8 i7 ae"
                  type="button"
                  onClick={() =>
                    updateCategoryFilter(
                      categoryFilter === 'my-posts' ? 'all' : 'my-posts',
                    )
                  }
                  style={{
                    border: `1px solid ${categoryFilter === 'my-posts' ? 'var(--brand-secondary)' : 'var(--gray-200)'}`,
                    backgroundColor:
                      categoryFilter === 'my-posts'
                        ? 'var(--palette-blue-50)'
                        : 'var(--palette-white)',}}
                >
                  {t.myPosts}
                </button>
              )}
            </div>
          </Column>
        )}

        {showLocationFilters && (
          <Column
            gap="0.5rem"
            alignItems={isMobile ? 'stretch' : 'flex-start'}
            style={{ minWidth: 0, width: '100%' }}
          >
            <Text.Medium isBold={true} style={{ flexShrink: 0 }}>
              {t.filterByLocation}
            </Text.Medium>
            <Select
              value={regionFilter}
              setValue={updateRegionFilter}
              options={regionOptions.map((region) => ({
                value: String(region.id),
                label: region.name,
              }))}
              notSelectedLabel={t.regionPlaceholder}
              containerStyle={{
                width: '100%',
                flexShrink: 0,
              }}
            />
          </Column>
        )}
      </div>

      {hasActiveFilters && (
        <Button
          variant="secondary"
          size="small"
          label={t.clearFilters}
          onClick={clearFilters}
          style={{ alignSelf: 'flex-start' }}
        />
      )}
    </Column>
  )
}
